/**
 * Hook for staking USDv to receive sUSDv
 */

import { type TransactionFlowConfig, type TransactionState, useTransactionFlow } from "@solomon-apps/shared";
import { DEFAULT_SALT, TOKEN_DECIMALS } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { SolomonStake } from "@solomon-labs/solomon-protocol";
import { useMemo } from "react";

/**
 * Return type for useStakeTransaction hook
 */
export interface UseStakeTransactionReturn {
    /** Current transaction state */
    state: TransactionState;
    /** Execute stake transaction */
    stake: (amount: BigDecimal) => Promise<string | null>;
    /** Reset state to initial */
    reset: () => void;
}

const INVALIDATE_KEYS = [["user-staking-stats"], ["staking-stats"], ["protocol-data"], ["transactionHistory"]];

/**
 * Hook for staking USDv to receive sUSDv
 *
 * Uses SolomonStake to create the transaction and ConnectorKit to sign/send.
 *
 * @param salt - Vault salt (default: main vault)
 * @returns Transaction state and execute function
 */
export function useStakeTransaction(salt: Uint8Array = DEFAULT_SALT): UseStakeTransactionReturn {
    const config = useMemo(
        (): TransactionFlowConfig<[BigDecimal]> => ({
            name: "useStakeTransaction",
            buildTransaction: async (
                [amount],
                { publicKey, recentBlockhash }
            ): Promise<{
                serialize(): Uint8Array;
            }> => {
                const rawAmount = amount.multiply(BigDecimal.pow10(TOKEN_DECIMALS.USDv)).toBigint();
                return SolomonStake.createStakeTransaction(publicKey, salt, rawAmount, {
                    feePayer: publicKey,
                    recentBlockhash
                });
            },
            invalidateQueryKeys: INVALIDATE_KEYS
        }),
        [salt]
    );

    const { state, execute, reset } = useTransactionFlow(config);

    return { state, stake: execute, reset };
}
